<script lang="ts">
import type { Sponsorship } from '@/types'
import { Genders } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'

import BaseCard from './new/BaseCard.vue'

/**
 * A card to display a GLP sponsorship.
 *
 */
export default defineComponent({
  components: { BaseCard },
  props: {
    /**
     * A configured sponsorship object.
     */
    sponsorship: {
      type: Object as PropType<Sponsorship>,
      required: true,
    },
    displayButton: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    return { Genders }
  },
  computed: {
    imageUrl() {
      return `url(${this.sponsorship.image})`
    },
  },
  methods: {
    goTo(event: MouseEvent) {
      if (!this.displayButton) {
        event.stopPropagation()
        void this.$router.push({
          name: 'good-causes',
        })
      }
    },
  },
})
</script>

<template>
  <BaseCard class="gap-0 p-0">
    <div
      class="grid overflow-hidden bg-primary bg-[url('@/assets/images/sponsorships-background.svg')] bg-cover bg-no-repeat text-white"
      :class="{
        'cursor-pointer': !displayButton,
        'cursor-default': displayButton,
      }"
      @click="(e) => goTo(e)"
    >
      <img
        :src="sponsorship.image"
        :alt="`A picture of ${sponsorship.name}`"
        class="col-start-1 col-end-1 row-start-1 -row-end-1 mx-auto mt-20 w-[283px]"
      />

      <div
        class="col-start-1 col-end-1 row-start-1 -row-end-1 flex h-full flex-col justify-between px-5 py-4 text-center"
      >
        <div class="dropshadow">
          <div class="text-xl font-bold">
            {{ sponsorship.name }}
          </div>
          <div class="text-lg font-medium">
            {{ sponsorship.profession }}
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div class="dropshadow h-line-3 text-balance font-semibold leading-5">
            "{{ sponsorship.quote }}"
          </div>

          <AppYouTubeModal
            v-if="displayButton"
            :id="sponsorship.youtube_id"
            :title="`${sponsorship.name}'s Story'`"
          >
            <template #default="{ openModal }">
              <AppButton
                class="button-secondary mx-auto mb-4 w-full border-white bg-primary/25 text-white transition-all duration-300 hover:bg-primary/75"
                :title="`Watch ${sponsorship.name}'s story`"
                :aria-label="`Watch ${sponsorship.name}'s story`"
                :icon="Icons.ArrowRight"
                @click="openModal"
              >
                Watch
                {{ sponsorship.gender === Genders.Female ? 'her' : 'his' }}
                story here
              </AppButton>
            </template>
          </AppYouTubeModal>
        </div>
      </div>
    </div>
  </BaseCard>
</template>
